/**
 * Credit Admin — Doctors Management.
 *
 * Lets credit_admin (and super_admin) add a new doctor and view all existing
 * doctors in the system.
 *
 *  - System generates a secure password and returns it ONCE to the admin.
 *  - WhatsApp share button pre-fills the message so the admin can deliver
 *    the credentials immediately (no email infra yet).
 *  - KVKK paper consent is captured as a required checkbox + timestamp.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../../components/layout';
import { creditAdminDoctorsApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
  Stethoscope,
  UserPlus,
  Loader2,
  Search,
  Copy,
  CheckCircle2,
  ShieldCheck,
  Mail,
  Phone,
  Building2,
  Eye,
  EyeOff,
  KeyRound,
} from 'lucide-react';

const initialForm = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  clinic_name: '',
  kvkk_paper_consent: false,
};

export default function CreditAdminDoctorsPage() {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  // success modal with one-time password + WhatsApp link
  const [created, setCreated] = useState(null);
  const [createdMode, setCreatedMode] = useState('create'); // 'create' | 'reset'
  const [showPwd, setShowPwd] = useState(false);
  const [copied, setCopied] = useState(false);
  const [resettingId, setResettingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await creditAdminDoctorsApi.list({
        limit: 100,
        ...(search ? { search } : {}),
      });
      setDoctors(res.items || []);
    } catch (e) {
      toast.error(e?.message || 'Doktorlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.kvkk_paper_consent) {
      toast.error('KVKK onayını işaretleyin.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await creditAdminDoctorsApi.create({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        clinic_name: form.clinic_name.trim() || null,
        kvkk_paper_consent: true,
      });
      setCreated(res);
      setCreatedMode('create');
      setOpenCreate(false);
      setForm(initialForm);
      load();
    } catch (err) {
      toast.error(err?.message || 'Doktor oluşturulamadı');
    } finally {
      setSubmitting(false);
    }
  };

  const onResetPassword = async (doctor) => {
    if (!doctor?.id) return;
    const ok = window.confirm(
      `Dr. ${doctor.name} için yeni şifre üretilecek ve eski şifresi geçersiz olacak. Devam edilsin mi?`
    );
    if (!ok) return;
    setResettingId(doctor.id);
    try {
      const res = await creditAdminDoctorsApi.resetPassword(doctor.id);
      setCreated(res);
      setCreatedMode('reset');
    } catch (err) {
      toast.error(err?.message || 'Şifre sıfırlanamadı');
    } finally {
      setResettingId(null);
    }
  };

  const copyPassword = async () => {
    if (!created?.generated_password) return;
    try {
      await navigator.clipboard.writeText(created.generated_password);
      setCopied(true);
      toast.success('Şifre kopyalandı');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Kopyalama başarısız');
    }
  };

  const copyCredentials = async () => {
    if (!created) return;
    const txt = `NeoBreed AI Giriş Bilgileri\n\nDoktor: ${created.name}\nE-posta: ${created.email}\nGeçici Şifre: ${created.generated_password}\n\nGiriş: https://neobreedai.com/login`;
    try {
      await navigator.clipboard.writeText(txt);
      toast.success('Tüm bilgiler kopyalandı');
    } catch {
      toast.error('Kopyalama başarısız');
    }
  };

  const filtered = doctors.filter((d) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (d.name || '').toLowerCase().includes(q) ||
      (d.email || '').toLowerCase().includes(q) ||
      (d.phone || '').toLowerCase().includes(q)
    );
  });

  return (
    <DashboardLayout
      title="Doktorlar"
      subtitle="Yeni doktor ekle ve mevcut doktorları yönet"
    >
      <div className="space-y-4" data-testid="credit-admin-doctors-page">
        {/* Toolbar */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="İsim, e-posta veya telefon ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="ca-doctors-search-input"
              />
            </div>
            <Button
              onClick={() => setOpenCreate(true)}
              className="bg-[#5A67D8] hover:bg-[#4338CA] text-white gap-2"
              data-testid="ca-doctors-add-btn"
            >
              <UserPlus className="w-4 h-4" />
              Doktor Ekle
            </Button>
          </CardContent>
        </Card>

        {/* List */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Yükleniyor...
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-slate-500 text-sm">
                <Stethoscope className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                {search ? 'Sonuç bulunamadı.' : 'Henüz doktor eklenmemiş.'}
              </div>
            ) : (
              <Table data-testid="ca-doctors-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Doktor</TableHead>
                    <TableHead>İletişim</TableHead>
                    <TableHead>Klinik</TableHead>
                    <TableHead>KVKK</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead className="text-right">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((d) => (
                    <TableRow key={d.id} data-testid={`ca-doctor-row-${d.id}`}>
                      <TableCell className="font-medium text-slate-800">
                        Dr. {d.name}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-slate-700 flex items-center gap-1.5">
                          <Mail className="w-3 h-3 text-slate-400" /> {d.email}
                        </div>
                        {d.phone && (
                          <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                            <Phone className="w-3 h-3 text-slate-400" /> {d.phone}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {d.clinic_name ? (
                          <span className="text-xs text-slate-600 flex items-center gap-1.5">
                            <Building2 className="w-3 h-3 text-slate-400" />
                            {d.clinic_name}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {d.kvkk_paper_consent ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1">
                            <ShieldCheck className="w-3 h-3" />
                            Kağıt Onay
                          </Badge>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {d.is_active ? (
                          <Badge className="bg-blue-50 text-blue-700 border-blue-200">
                            Aktif
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600">Pasif</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onResetPassword(d)}
                          disabled={resettingId === d.id}
                          className="gap-1.5"
                          data-testid={`ca-doctor-reset-pwd-${d.id}`}
                          title="Şifre sıfırla ve yeni şifreyi WhatsApp ile gönder"
                        >
                          {resettingId === d.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <KeyRound className="w-3.5 h-3.5" />
                          )}
                          Şifre Sıfırla
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Create Dialog ── */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-md" data-testid="ca-doctors-create-dialog">
          <DialogHeader>
            <DialogTitle>Yeni Doktor Ekle</DialogTitle>
            <DialogDescription>
              Şifre otomatik oluşturulur. Tek seferlik gösterilir.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ca-first">Ad *</Label>
                <Input
                  id="ca-first"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  required
                  data-testid="ca-doctor-first-name"
                />
              </div>
              <div>
                <Label htmlFor="ca-last">Soyad *</Label>
                <Input
                  id="ca-last"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  required
                  data-testid="ca-doctor-last-name"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="ca-email">E-posta *</Label>
              <Input
                id="ca-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                data-testid="ca-doctor-email"
              />
            </div>
            <div>
              <Label htmlFor="ca-phone">Telefon * <span className="text-slate-400">(WhatsApp için)</span></Label>
              <Input
                id="ca-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="05XX XXX XX XX"
                required
                data-testid="ca-doctor-phone"
              />
            </div>
            <div>
              <Label htmlFor="ca-clinic">Klinik Adı <span className="text-slate-400">(opsiyonel)</span></Label>
              <Input
                id="ca-clinic"
                value={form.clinic_name}
                onChange={(e) => setForm({ ...form, clinic_name: e.target.value })}
                data-testid="ca-doctor-clinic"
              />
            </div>
            <label className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 cursor-pointer">
              <input
                type="checkbox"
                checked={form.kvkk_paper_consent}
                onChange={(e) =>
                  setForm({ ...form, kvkk_paper_consent: e.target.checked })
                }
                className="mt-0.5"
                data-testid="ca-doctor-kvkk-checkbox"
              />
              <div className="text-xs text-amber-900 leading-relaxed">
                <strong>Kağıt KVKK onayı alındı.</strong> Doktorun ıslak imzalı KVKK
                aydınlatma metnini elimde bulunduruyorum. ({new Date().toLocaleDateString('tr-TR')})
              </div>
            </label>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpenCreate(false)}
                disabled={submitting}
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={submitting || !form.kvkk_paper_consent}
                className="bg-[#5A67D8] hover:bg-[#4338CA] text-white"
                data-testid="ca-doctor-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Oluşturuluyor...
                  </>
                ) : (
                  'Doktoru Oluştur'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Success Modal (One-Time Password Reveal) ── */}
      <Dialog open={!!created} onOpenChange={(v) => !v && setCreated(null)}>
        <DialogContent className="max-w-md" data-testid="ca-doctor-success-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" />
              {createdMode === 'reset' ? 'Şifre sıfırlandı' : 'Doktor oluşturuldu'}
            </DialogTitle>
            <DialogDescription>
              Şifre yalnızca bir kez gösterilir. Lütfen şimdi paylaşın.
            </DialogDescription>
          </DialogHeader>

          {created && (
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 p-3 bg-slate-50 space-y-1">
                <p className="text-sm font-semibold text-slate-800">
                  Dr. {created.name}
                </p>
                <p className="text-xs text-slate-600 flex items-center gap-1">
                  <Mail className="w-3 h-3" /> {created.email}
                </p>
                <p className="text-xs text-slate-600 flex items-center gap-1">
                  <Phone className="w-3 h-3" /> {created.phone}
                </p>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">
                  Geçici Şifre
                </Label>
                <div className="mt-1 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
                  <code
                    className="flex-1 font-mono text-base text-amber-900 select-all"
                    data-testid="ca-doctor-generated-password"
                  >
                    {showPwd ? created.generated_password : '••••••••••••'}
                  </code>
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="p-1.5 rounded hover:bg-amber-100"
                    data-testid="ca-doctor-toggle-pwd"
                    aria-label={showPwd ? 'Gizle' : 'Göster'}
                  >
                    {showPwd ? (
                      <EyeOff className="w-4 h-4 text-amber-900" />
                    ) : (
                      <Eye className="w-4 h-4 text-amber-900" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={copyPassword}
                    className="p-1.5 rounded hover:bg-amber-100"
                    data-testid="ca-doctor-copy-pwd-btn"
                    aria-label="Şifreyi kopyala"
                  >
                    {copied ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-amber-900" />
                    )}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2">
                <a
                  href={created.whatsapp_share_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-lg bg-[#25D366] hover:bg-[#1DAA53] text-white font-medium transition-colors"
                  data-testid="ca-doctor-whatsapp-share-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.04 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.886 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.49-8.413z"/>
                  </svg>
                  WhatsApp ile gönder
                </a>
                <Button
                  type="button"
                  variant="outline"
                  onClick={copyCredentials}
                  className="w-full gap-2"
                  data-testid="ca-doctor-copy-all-btn"
                >
                  <Copy className="w-4 h-4" />
                  Tüm bilgileri kopyala
                </Button>
              </div>

              <p className="text-[11px] text-slate-500 leading-relaxed">
                Bu pencereyi kapattıktan sonra şifre tekrar gösterilmez. Doktor şifresini
                ileride unutursa Süper Admin'den sıfırlama talep etmelidir.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={() => {
                setCreated(null);
                setShowPwd(false);
              }}
              className="w-full"
              data-testid="ca-doctor-success-close-btn"
            >
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
